import { ArrowDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";

export function ClosedCaptionsDisplay() {
  const isCaptionEnabled = useVidyoStore(
    (state) => state.closedCaptioning.enabled,
  );
  const messages = useVidyoStore((state) => state.closedCaptioning.messages);

  const containerRef = useRef<HTMLDivElement>(null);
  // Track if auto-scroll is enabled
  const [isAutoScroll, setIsAutoScroll] = useState(true);

  // Scroll to bottom if auto-scroll is enabled
  useEffect(() => {
    if (isAutoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages, isAutoScroll]);

  // Handler for user scroll
  const handleScroll = () => {
    const container = containerRef.current;
    if (!container) return;
    // If user is at the bottom, enable auto-scroll
    const isAtBottom =
      Math.abs(
        container.scrollHeight - container.scrollTop - container.clientHeight,
      ) < 5;
    if (isAtBottom && !isAutoScroll) {
      setIsAutoScroll(true);
    } else if (!isAtBottom && isAutoScroll) {
      setIsAutoScroll(false);
    }
  };

  // Handler for reset button
  const handleReset = () => {
    setIsAutoScroll(true);
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  };

  if (!isCaptionEnabled) return null;

  return (
    <div className="w-full">
      {/* Reset button appears only when auto-scroll is disabled */}

      <ScrollArea
        ref={containerRef}
        className="relative mx-auto h-56 w-full max-w-3xl overflow-y-auto p-4 text-2xl text-foreground"
        onScroll={handleScroll}
      >
        {!isAutoScroll && (
          <Button
            variant="outline"
            className="absolute bottom-4 right-4"
            onClick={handleReset}
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
        )}
        <div className="flex flex-col gap-y-2">
          {messages.map((message) => (
            <div key={`message-${message.timestamp.toISOString()}`}>
              <p className="font-thin">
                <span className="font-bold">{message.name}</span>:{" "}
                {message.text}
              </p>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
